import { displayPlayerName } from 'src/app/core/util/player-name/player-name.util';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NPCGameService, TRAINING_ABORT_MESSAGES } from './npc-game.service';
import { ReplayService } from './replay.service';
import { MyPlayerService } from './my-player.service';
import { TableStatusEnum } from 'src/app/core/enums';
import { SocketIoService, MdbToastService } from 'src/app/core/services';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { BehaviorSubject, firstValueFrom, Observable, map } from 'rxjs';
import { GameStateService } from './game-state.service';
import { LobbyService } from './lobby.service';
import * as SoundService from 'src/app/core/services/sound/sound.service';
import { InfoDialogService } from './info-dialog.service';
import { GameEventService } from './game-event.service';
import { PlayerInterface } from 'src/app/core/interfaces';
import { AppExtensionsService } from './app-extensions.service';
import { MdbModalService } from 'mdb-angular-ui-kit/modal';
import { LeaveGameConfirmModalComponent } from '../components/leave-game-confirm-modal/leave-game-confirm-modal.component';
import { MatchmakingService } from './matchmaking.service';
import { LobbyBackendService } from 'src/app/core/backend-services/lobby/lobby.backend-service';
import { GamePlayService } from './game-play.service';
import { wait } from 'src/app/core/util';
import { DialogUtilService } from 'src/app/core/util/dialog/dialog.util';

@Injectable({
  providedIn: 'root',
})
export class GameShellService {
  private hasLoadedSource: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );
  public hasLoaded$ = this.hasLoadedSource.asObservable();
  private showLoadingAnimationSource: BehaviorSubject<boolean> =
    new BehaviorSubject(true);
  public showLoadingAnimation$ = this.showLoadingAnimationSource.asObservable();
  private currentViewSource: BehaviorSubject<'game' | 'lobby'> =
    new BehaviorSubject('lobby');
  public currentView$ = this.currentViewSource.asObservable();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private npcGameService: NPCGameService,
    private replayService: ReplayService,
    private myPlayer: MyPlayerService,
    private socketIO: SocketIoService,
    private state: GameStateService,
    private infoDialogService: InfoDialogService,
    private lobbyService: LobbyService,
    private gameEventService: GameEventService,
    private appExtensionsService: AppExtensionsService,
    private toast: MdbToastService,
    private modalService: MdbModalService,
    private matchmakingService: MatchmakingService,
    private gamePlayService: GamePlayService,
    private lobbyBackendService: LobbyBackendService,
    private authService: AuthService,
    private dialogUtil: DialogUtilService
  ) {}

  public get currentView(): 'game' | 'lobby' {
    return this.currentViewSource.getValue();
  }

  public get hasLoaded(): boolean {
    return this.hasLoadedSource.getValue();
  }

  public get showLoadingAnimation(): boolean {
    return this.showLoadingAnimationSource.getValue();
  }

  /**
   * Seated at a live table whose game runs (no training, no replay). After a reconnect this
   * decides about re-entering, not the view: a gameStart sent while the socket was down is
   * lost, so the lobby may be on screen although the game runs.
   */
  public get seatedAtRunningTable(): boolean {
    if (this.npcGameService.isRunning || this.replayService.replayRunning) return false;
    const player = this.myPlayer.player;
    return !!player?.tableID && player.table?.status === TableStatusEnum.RUNNING;
  }

  /**
   * Counts started games (gameStart, spectatingStarted). Handlers that await before leaving
   * the table view check it: a game that started meanwhile is not theirs to end.
   */
  private gameStartEpoch = 0;

  public get leaveTableMarked(): boolean {
    return !!this.myPlayer.player?.wantToLeaveAfterBummerl;
  }

  public get leaveTableMarked$(): Observable<boolean> {
    return this.myPlayer.player$.pipe(
      map((player) => !!player?.wantToLeaveAfterBummerl)
    );
  }

  public async init(): Promise<void> {
    if (!this.hasLoaded) {
      this.setupHandlers();
      this.hasLoadedSource.next(true);
    }

    if (
      this.myPlayer.player.tableID &&
      this.myPlayer.player.table &&
      this.myPlayer.player.table.status === TableStatusEnum.RUNNING
    ) {
      await this.reEnter();
    } else if (this.route.snapshot.queryParamMap.get('replay')) {
      this.showGame();
      this.replayService.replayRound(
        this.route.snapshot.queryParamMap.get('replay')
      );
    } else if (this.route.snapshot.queryParamMap.get('training') === 'true') {
      const players = parseInt(
        this.route.snapshot.queryParamMap.get('players') || '2'
      ) as 2 | 3 | 4;
      const difficulty =
        (this.route.snapshot.queryParamMap.get('difficulty') as
          | 'easy'
          | 'medium'
          | 'hard') || 'medium';
      await this.startNPCGame(players, difficulty);
    } else if (
      this.route.snapshot.queryParamMap.get('createPrivate') === 'true'
    ) {
      const players = parseInt(
        this.route.snapshot.queryParamMap.get('players') || '2'
      );
      await this.createPrivateTable(players);
    } else if (this.route.snapshot.queryParamMap.get('quickMatch') === 'true') {
      const players = parseInt(
        this.route.snapshot.queryParamMap.get('players') || '2'
      ) as 2 | 3 | 4;
      await this.startQuickMatch(players);
    } else if (this.myPlayer.player.watchingTableID) {
      await this.reEnterSpectatorGame();
    } else {
      this.showLobby();
      void this.offerTrainingResume();
    }

    setTimeout(() => {
      this.showLoadingAnimationSource.next(false);
    }, 350);
  }

  /**
   * A reloaded or closed game window loses the training on screen, but the server keeps
   * it (6 h). Asks whether to continue it; "Verwerfen" abandons it, closing the dialog
   * keeps it for later. Nothing is asked once the player started something else.
   */
  private async offerTrainingResume(): Promise<void> {
    const game = await this.npcGameService.findUnfinishedGame();
    if (!game) return;
    await wait(400); // after the loading screen
    if (!this.mayOfferResume()) return;
    const opponents = game.players.slice(1).map(player => player.username).filter(Boolean);
    const against = opponents.length > 1
      ? `${opponents.slice(0, -1).join(', ')} und ${opponents[opponents.length - 1]}`
      : opponents[0] || 'den Computer';
    const choice = await this.dialogUtil.showChoice(
      `Dein Training gegen ${against} ist noch nicht fertig gespielt. Möchtest du es fortsetzen?`,
      { title: 'Training fortsetzen?', confirmLabel: 'Fortsetzen', cancelLabel: 'Verwerfen' }
    );
    if (choice === null || !this.mayOfferResume()) return;
    if (!choice) {
      this.npcGameService.discardUnfinishedGame(game);
      return;
    }
    this.showGame();
    void this.npcGameService.resumeNPCGame(game);
  }

  /** still idle in the lobby: no training, table, match search or replay started meanwhile */
  private mayOfferResume(): boolean {
    return (
      this.currentView === 'lobby' &&
      !this.npcGameService.isRunning &&
      !this.replayService.replayRunning &&
      !this.matchmakingService.state.isInQueue &&
      !this.myPlayer.player.tableID &&
      !this.myPlayer.player.watchingTableID
    );
  }

  public async startNPCGame(
    numPlayers: 2 | 3 | 4 = 2,
    difficulty: 'easy' | 'medium' | 'hard' | 'expert' = 'medium'
  ) {
    this.showGame();
    this.npcGameService.playNPCGame(numPlayers, difficulty);

    // Clear URL parameters after successful training game start
    if (this.route.snapshot.queryParamMap.get('training') === 'true') {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {},
        replaceUrl: true,
      });
    }
  }

  public async createPrivateTable(numPlayers: number = 2) {
    this.showLobby();
    await this.lobbyService.createPrivateTable(numPlayers);

    // Clear URL parameters after successful private table creation
    if (this.route.snapshot.queryParamMap.get('createPrivate') === 'true') {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {},
        replaceUrl: true,
      });
    }
  }

  public async startQuickMatch(numPlayers: 2 | 3 | 4 = 2) {
    this.showLobby();
    await this.matchmakingService.joinQueue(numPlayers);

    // Clear URL parameters after successful quick match start
    if (this.route.snapshot.queryParamMap.get('quickMatch') === 'true') {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {},
        replaceUrl: true,
      });
    }
  }

  public async checkAndLeaveGameTable() {
    if (this.npcGameService.isRunning) {
      this.npcGameService.endGame();
      this.showLobby();
    } else {
      let shouldPunishServer = false;
      try {
        shouldPunishServer = await this.socketIO.get('game/check-leave-punish');
      } catch (e) {
        console.info(e);
      }

      if (!shouldPunishServer && this.state.gameWillEnd) {
        // Game is ending soon, can leave immediately without penalty
        await this.leaveGameTable();
        return;
      } else if (!shouldPunishServer && !this.leaveTableMarked) {
        // No penalty but game is still running - show confirmation modal
        const modalRef = this.modalService.open(
          LeaveGameConfirmModalComponent,
          {
            modalClass: 'modal-dialog-centered',
          }
        );

        const result = await firstValueFrom(modalRef.onClose);
        if (result) {
          await this.leaveGameTable();
          return;
        } else {
          // User cancelled, don't do anything more
          return;
        }
      }

      // Only reach here if shouldPunishServer is true OR user already has leave marked
      // Toggle the leave marking for next game
      const newWantToLeave = !this.leaveTableMarked;
      await this.lobbyBackendService.toggleWantToLeave(newWantToLeave);
      // Update local player state after server call
      await this.myPlayer.update();

      if (newWantToLeave) {
        this.toast.success(
          'Du wirst nach dem aktuellen Bummerl den Tisch verlassen.'
        );
      } else {
        this.toast.success('Du bleibst im Spiel. Viel Erfolg!');
      }
    }
  }

  /** "Spielen" always opens the tables, also for the next account in this tab. */
  public resetLobbyRoom() {
    void this.lobbyService.moveToStandardRoom();
  }

  public async leaveGameTable() {
    await this.lobbyService.leaveTable();
  }

  private showLobby(silent: boolean = false) {
    console.log('Showing lobby.');
    this.currentViewSource.next('lobby');
    SoundService.stopAllSounds();
    if (!silent) SoundService.playRandomSFX('goodbye');
    this.infoDialogService.hideInfoDialog();
    this.gameEventService.emit({ name: 'enteredLobby', data: {} });

    if (!silent) {
      // Show ad if needed
      if (!this.lobbyService.sitsOnTable()) {
        setTimeout(() => {
          this.appExtensionsService.maybeShowAd();
        }, 500);
      }
    }
  }

  private showGame(silent: boolean = false) {
    console.log('Showing game.');
    this.currentViewSource.next('game');
    if (!silent) SoundService.playRandomSFX('servus');
    this.infoDialogService.hideInfoDialog();
    this.myPlayer.updateOnlineStatus();
  }

  private setupHandlers() {
    // The training ended on its own (gone / replaced on the server, or no connection for
    // too long): it is already ended locally, tell the player and leave the table view.
    this.npcGameService.aborted$.subscribe((reason) => {
      this.toast.warning(TRAINING_ABORT_MESSAGES[reason]);
      // logged out on the server: the regular re-login flow (logged-out state, start page
      // with the sign-in options) instead of the lobby, which needs a session. Named reason:
      // a guest who is playing does not expire, so this is no guest expiry.
      if (reason === 'session') void this.authService.handleServerLogout('session');
      else if (this.currentView === 'game') this.showLobby(true);
    });
    this.socketIO.on('gameStart', () => {
      this.gameStartEpoch++;
      this.state.reset(true);
      this.state.setPlayers(
        this.myPlayer.player.table.members,
        this.myPlayer.player.table.members
          .map((el) => el.userID)
          .indexOf(this.myPlayer.player.userID)
      );
      this.showGame();
    });
    this.socketIO.on('spectatingStarted', (data) => {
      this.gameStartEpoch++;
      this.state.reset(true);
      this.state.isSpectatorGame = true;
      this.state.setPlayers(data.data.table.members, -1);
      this.showGame();
    });
    this.socketIO.on('endGameUserLeft', async (data) => {
      try {
        await this.endGameUserLeft(data.data.player);
      } catch (e) {
        console.warn('endGameUserLeft handler failed:', e);
      }
    });
    this.socketIO.on('tableUpdate', async (data) => {
      if (this.currentView === 'game' && this.state.isSpectatorGame) return;

      try {
        data = data.data;
        const me = this.myPlayer.player.userID;
        const table = data.table;
        // Decide from the state at arrival. The table refresh below awaits HTTP reads of the
        // members, and the next events may run meanwhile: switching tables sends "removed
        // from A", then "joined B" and, if B is full, gameStart of B. A view check after the
        // await saw B's game and ended it as if A's were on screen (stuck in the lobby
        // without cards while B was played).
        const atThisTable = !!table?.id && this.myPlayer.player.tableID === table.id;
        const endsShownTable = !!data.removedPlayer && this.currentView === 'game' && atThisTable;
        const epoch = this.gameStartEpoch;

        if (table?.members?.some((member) => member?.userID === me)) {
          await this.myPlayer.setMyTable(table);
        } else if (atThisTable) {
          // I left this table (maybe for another one): it is no longer mine
          await this.myPlayer.setMyTable(undefined);
        }
        // else: a late update of a table I already left; my current table stays
        if (data.newPlayer) {
          if (data.newPlayer.userID === this.myPlayer.player.userID) {
            console.log('You sat down table');
            this.gameEventService.emit({
              name: 'satDownTable',
              data: data.newPlayer,
            });
            this.lobbyService.isCreatingTable = false;
          } else {
            console.log('Someone sat down table', data.newPlayer.username);
          }
        }
        if (data.removedPlayer) {
          if (data.removedPlayer.userID === this.myPlayer.player.userID) {
            console.log('You left table - clearing matchmaking state');
            this.gameEventService.emit({
              name: 'leftTable',
              data: data.removedPlayer,
            });
            this.lobbyService.isLeavingTable = false;

            // Always clear matchmaking state when current player is removed from table
            // This handles the case where players are automatically removed after matchmaking games end
            await this.matchmakingService.clearQueueState();
          } else {
            console.log('Someone left table', data.removedPlayer.username);
          }

          if (endsShownTable) {
            // when current view is table (game) view and a user leaves the table, go back to room view
            // this is normally handled by the endGameUserLeft handler, however if no game is being played,
            // but the user is still in table view when a player on that table leaves, the view should
            // go back to rooms view

            // just call the endGameUserLeft handler
            await this.endGameUserLeft(data.removedPlayer, epoch);
          }
        }
      } catch (e) {
        console.warn('tableUpdate handler failed:', e);
      }
    });
    this.socketIO.on('userMessage', (data) => {
      if (data.receiverUserID === this.myPlayer.player.userID) {
        this.toast.info('Du hast eine neue Nachricht erhalten');
      }
    });
    this.socketIO.on('wantToLeaveAfterBummerlUpdate', async (data) => {
      try {
        // Update local player state when wantToLeaveAfterBummerl changes
        await this.myPlayer.update();
      } catch (e) {
        console.warn('wantToLeaveAfterBummerlUpdate handler failed:', e);
      }
    });
    this.socketIO.on('notStartingNewGame', async () => {
      console.log('got notStartingNewGame');
      const epoch = this.gameStartEpoch;
      await wait(4000 + 3000); // wait for all game end stuff to be done, also because we wait 4000 seconds before we event emit gameend

      if (this.currentView !== 'game' || !this.state.gameWillEnd || epoch !== this.gameStartEpoch) {
        console.log(
          'Already exited from game, not showing notStartingNewGame dialog'
        );
        return;
      }

      try {
        console.log('now handling notStartingNewGame ..');
        this.infoDialogService.showInfoDialog(
          'Tisch wird verlassen',
          'Du verlässt den Tisch.'
        );

        await wait(1500);

        await this.myPlayer.update();

        // Ensure frontend matchmaking state is cleared when game ends due to user leaving
        await this.matchmakingService.clearQueueState();

        if (this.currentView === 'game' && epoch === this.gameStartEpoch) {
          this.showLobby();
        }
      } catch (e) {
        console.warn('notStartingNewGame handler failed:', e);
      }
    });

  }

  /** `epoch`: the game this ends; a game that started meanwhile (e.g. at another table) stays */
  private async endGameUserLeft(player: PlayerInterface, epoch = this.gameStartEpoch): Promise<void> {
    if (epoch !== this.gameStartEpoch) return;
    this.infoDialogService.showInfoDialog(
      'Das Spiel wurde beendet.',
      `${displayPlayerName(player)} hat das Spiel verlassen.`
    );

    await this.myPlayer.update();

    // Ensure frontend matchmaking state is cleared when game ends due to user leaving
    await this.matchmakingService.clearQueueState();

    if (this.currentView === 'game' && epoch === this.gameStartEpoch) {
      this.showLobby();
    }
  }

  private async reEnter(): Promise<void> {
    await this.gamePlayService.restoreState();
  }

  private async reEnterSpectatorGame(): Promise<void> {
    await this.socketIO.post('game/watch', {
      tableID: this.myPlayer.player.watchingTableID,
    });
  }

  public async refreshGameAfterDisconnect(): Promise<void> {
    // Training and replay have no live table to re-enter.
    if (this.npcGameService.isRunning || this.replayService.replayRunning) return;
    this.showLobby(true);
    if (this.myPlayer.player.watchingTableID) await this.reEnterSpectatorGame();
    else if (this.myPlayer.player.tableID) await this.reEnter();
    else this.showLobby();
  }
}
